import React, { useState } from 'react';
import { Calendar, MapPin, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { useFetchClientBookings } from '@/hooks/useClients';

interface BookingsTabProps {
  clientId: string;
}

interface BookingRow {
  id: string;
  name: string | null;
  start_date: string | null;
  end_date: string | null;
  status_code: string | null;
  venue_name: string | null;
}

function statusBadge(code: string | null) {
  switch (code) {
    case 'confirmed':
      return { label: 'Confirmed', cls: 'text-led-green bg-led-green/10' };
    case 'pending':
      return { label: 'Pending', cls: 'text-led-cyan bg-led-cyan/10' };
    case 'completed':
      return { label: 'Completed', cls: 'text-muted-foreground bg-surface-3/50' };
    case 'cancelled':
      return { label: 'Cancelled', cls: 'text-led-red bg-led-red/10' };
    case 'invoiced':
      return { label: 'Invoiced', cls: 'text-led-purple bg-led-purple/10' };
    default:
      return { label: code || 'Unknown', cls: 'text-muted-foreground bg-surface-3/50' };
  }
}

const PAST_PAGE_SIZE = 50;

export const BookingsTab: React.FC<BookingsTabProps> = ({ clientId }) => {
  const { data: bookings = [], isLoading, error } = useFetchClientBookings(clientId);
  const [pastDisplayCount, setPastDisplayCount] = useState(PAST_PAGE_SIZE);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive py-4">Failed to load bookings.</p>;
  }

  // Split into upcoming and past
  const now = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter(
    (b) => b.start_date && b.start_date >= now && b.status_code !== 'completed' && b.status_code !== 'cancelled'
  );
  const upcomingIds = new Set(upcoming.map((b) => b.id));
  const past = bookings.filter((b) => !upcomingIds.has(b.id));

  const renderBooking = (booking: BookingRow) => {
    const status = statusBadge(booking.status_code);
    return (
      <div
        key={booking.id}
        className="flex items-center gap-3 bg-surface-1 border border-border rounded px-3 py-2.5"
      >
        <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-heading uppercase tracking-wider text-foreground truncate">
            {booking.name || 'Untitled Booking'}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground mt-0.5">
            {booking.start_date && (
              <span className="font-mono whitespace-nowrap">
                {format(new Date(booking.start_date), 'MMM d, yyyy')}
                {booking.end_date && booking.end_date !== booking.start_date && (
                  <> - {format(new Date(booking.end_date), 'MMM d')}</>
                )}
              </span>
            )}
            {booking.venue_name && (
              <span className="flex items-center gap-1 truncate max-w-[200px] sm:max-w-none">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{booking.venue_name}</span>
              </span>
            )}
          </div>
        </div>
        <span
          className={cn(
            'text-[10px] font-label uppercase tracking-wider px-2 py-0.5 rounded border border-current/20 flex-shrink-0',
            status.cls
          )}
        >
          {status.label}
        </span>
      </div>
    );
  };

  if (bookings.length === 0) {
    return (
      <div className="text-center py-8 space-y-1">
        <Calendar className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">No bookings recorded for this client.</p>
        <p className="text-xs text-muted-foreground">Bookings will appear here once created.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {upcoming.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-heading text-xs uppercase tracking-wider text-primary">Upcoming</h3>
          {upcoming.map(renderBooking)}
        </div>
      )}
      {past.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-heading text-xs uppercase tracking-wider text-muted-foreground">Past</h3>
          {past.slice(0, pastDisplayCount).map(renderBooking)}
          {past.length > pastDisplayCount && (
            <button
              onClick={() => setPastDisplayCount((c) => c + PAST_PAGE_SIZE)}
              className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Load more ({past.length - pastDisplayCount} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
};
